import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  DialogContent,
  IconButton,
  ListItemIcon,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  Block,
  Delete,
  DeleteOutline,
  Edit,
  PointOfSale,
  Refresh,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate } from 'react-router-dom';
import TableView from '../../components/TableView';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  MolePrimaryButton,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useData } from '../../hooks/useData';
import { periodRequestConfig } from '../../hooks/useDateRange';
import { Util } from '../../util/Util';
import { LocalStorageKeys } from '../../util/constants';
import OfferModal from './OfferModal';
import OfferReport from './OfferReport';
import { OfferProvider } from './OfferContext';
import { clickableRef } from '../deposit/clickableRef';
import {
  offerKindI18nKey,
  offerStatusI18nKey,
  type OfferRow,
} from './offerTypes';
import { canAmend, canDrop, canRecord } from '../../util/access';

type PosOption = { id: number; DepotName?: string; depot_name?: string };

const Offer = () => {
  const { api, access } = useAuth();
  const { queueReRun, t, setActiveDepot } = useUI();
  const navigate = useNavigate();
  const themeColor = useMoleThemeColor();
  const {
    data,
    lastUpdate,
    loadFromIndexedDB,
    saveFromServer,
    dateRange: period,
    setDateRange: setPeriod,
    loading,
    setLoading,
  } = useData<OfferRow>(LocalStorageKeys.OFFER_RECORDS);
  const [postOpen, setPostOpen] = useState(false);
  const [editing, setEditing] = useState<OfferRow | null>(null);
  const [toDelete, setToDelete] = useState<OfferRow | null>(null);
  const [toCancel, setToCancel] = useState<OfferRow | null>(null);
  const [toInvoice, setToInvoice] = useState<OfferRow | null>(null);
  const [toView, setToView] = useState<OfferRow | null>(null);
  const [posOptions, setPosOptions] = useState<PosOption[]>([]);
  const [selectedPos, setSelectedPos] = useState<number | ''>('');
  const firstFetchRef = useRef(false);
  const toastIdRef = useRef<string | number>(-1);

  const canCreate = canRecord(access, 'offer');
  const canEdit = canAmend(access, 'offer');
  const canDelete = canDrop(access, 'offer');
  const canSale = canRecord(access, 'sale');

  const fetchData = async () => {
    setLoading(true);
    const res = await api.get('/offers', periodRequestConfig(period));
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      saveFromServer(rows, period);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchData);
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadFromIndexedDB();
      if (cancelled || firstFetchRef.current) return;
      firstFetchRef.current = true;
      if (!loaded) void fetchData();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPos = async () => {
    const res = await api.get('depot/sales/');
    if (res?.status === 200) {
      setPosOptions(Array.isArray(res.data) ? res.data : []);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(fetchPos);
    }
  };

  useEffect(() => {
    if (toInvoice) {
      setSelectedPos('');
      void fetchPos();
    }
  }, [toInvoice]);

  const handleDelete = async () => {
    if (!toDelete) return;
    const res = await api.delete(`/offers/${toDelete.batch}`);
    if (res?.status === 204) {
      setToDelete(null);
      void fetchData();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleDelete);
    }
  };

  const handleCancel = async () => {
    if (!toCancel) return;
    const res = await api.post(`/offers/${toCancel.batch}/cancel`);
    if (res?.status === 200) {
      setToCancel(null);
      void fetchData();
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleCancel);
    }
  };

  const handleInvoice = async () => {
    if (!toInvoice || !selectedPos) return;
    const res = await api.post(`/offers/${toInvoice.batch}/pos`, { pos: selectedPos });
    if (res?.status === 200) {
      setActiveDepot(Number(selectedPos));
      const src = '/offers';
      const id = toInvoice.batch;
      setToInvoice(null);
      navigate('/pos', { state: { offer: { id, posId: Number(selectedPos) }, src } });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(handleInvoice);
    }
  };

  const columns = useMemo(
    () => [
      { header: t('common:tables.date'), accessorKey: 'date' },
      {
        header: t('common:tables.reference'),
        accessorKey: 'reference',
        Cell: ({ cell }: any) => clickableRef(String(cell.getValue() || ''), () => setToView(cell.row.original)),
      },
      {
        header: t('common:offer.kind'),
        accessorKey: 'kind',
        Cell: ({ cell }: any) => t(offerKindI18nKey(cell.getValue())),
      },
      {
        header: t('common:offer.status'),
        accessorKey: 'status',
        Cell: ({ cell }: any) => {
          const status = String(cell.getValue() || 'OPEN');
          const color = status === 'CONVERTED' ? 'success' : status === 'CANCELLED' ? 'default' : 'info';
          return <Chip size="small" color={color as any} label={t(offerStatusI18nKey(status))} />;
        },
      },
      { header: t('common:tables.client'), accessorKey: 'client' },
      {
        header: t('common:tables.depot'),
        accessorKey: 'depot',
        Cell: ({ cell }: any) => String(cell.getValue() || '') || '—',
      },
      {
        header: t('common:offer.plannedAmount'),
        accessorKey: 'planned_amount',
        Cell: ({ cell }: any) => Util.formatNumber(Number(cell.getValue() || 0)),
      },
      {
        header: t('common:offer.invoicedAmount'),
        accessorKey: 'invoiced_amount',
        Cell: ({ cell }: any) => Util.formatNumber(Number(cell.getValue() || 0)),
      },
      {
        header: t('common:tables.user'),
        accessorKey: 'user',
        Cell: ({ cell }: any) => String(cell.getValue() || '') || '—',
      },
    ],
    [t],
  );

  return (
    <OfferProvider>
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableView
				stateKey={LocalStorageKeys.OFFER_RECORDS}
				posting="offer"
          dataScope="offer"
          header={t('common:offer.title')}
          data={data}
          columns={columns as any}
          lastUpdated={lastUpdate}
          period={period}
          setDateRangeFilter={setPeriod}
          isLoading={loading}
          totals={[
            { key: 'planned_amount', label: t('common:offer.plannedAmount') },
            { key: 'invoiced_amount', label: t('common:offer.invoicedAmount') },
          ]}
          renderTopToolbarActions={() => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Tooltip title={t('common:ui.tooltips.refreshData')}>
                <IconButton onClick={() => void fetchData()} disabled={loading} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreate && (
                <Tooltip title={t('common:offer.postTitle')}>
                  <IconButton color="success" onClick={() => { setEditing(null); setPostOpen(true); }}>
                    <AddCircleOutline />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          renderRowActionMenuItems={(row, closeMenu) => [
            canEdit && String(row.original.status) === 'OPEN' ? (
              <MenuItem
                key="edit"
                onClick={() => { setEditing(row.original); setPostOpen(true); closeMenu(); }}
              >
                <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.edit')}
              </MenuItem>
            ) : null,
            canSale && String(row.original.status) === 'OPEN' ? (
              <MenuItem
                key="invoice"
                onClick={() => { setToInvoice(row.original); closeMenu(); }}
                sx={{ color: 'success.main' }}
              >
                <ListItemIcon sx={{ color: 'success.main' }}><PointOfSale fontSize="small" /></ListItemIcon>
                {t('common:offer.invoiceOnPos')}
              </MenuItem>
            ) : null,
            canEdit && String(row.original.status) === 'OPEN' ? (
              <MenuItem
                key="cancel"
                onClick={() => { setToCancel(row.original); closeMenu(); }}
                sx={{ color: 'warning.main' }}
              >
                <ListItemIcon sx={{ color: 'warning.main' }}><Block fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.cancel')}
              </MenuItem>
            ) : null,
            canDelete && String(row.original.status) !== 'CONVERTED' ? (
              <MenuItem
                key="delete"
                onClick={() => { setToDelete(row.original); closeMenu(); }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}><Delete fontSize="small" /></ListItemIcon>
                {t('common:ui.buttons.delete')}
              </MenuItem>
            ) : null,
          ]}
        />
      </LocalizationProvider>

      <OfferModal
        isOpen={postOpen}
        bundle={editing}
        toggle={() => { setPostOpen(false); setEditing(null); }}
        onSuccess={() => void fetchData()}
      />

      <OfferReport
        isOpen={Boolean(toView)}
        bundle={toView}
        toggle={() => setToView(null)}
      />

      <MoleDialog open={Boolean(toInvoice)} onClose={() => setToInvoice(null)} maxWidth="sm" fullWidth themeColor={themeColor}>
        <MoleDialogHeader
          title={t('common:offer.invoiceOnPos')}
          subtitle={t('common:offer.selectPosHint')}
          icon={<PointOfSale />}
          onClose={() => setToInvoice(null)}
          themeColor={themeColor}
        />
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography>{toInvoice?.reference}</Typography>
            <TextField
              select
              size="small"
              label={t('common:offer.selectPos')}
              value={selectedPos}
              onChange={(event) => setSelectedPos(event.target.value === '' ? '' : Number(event.target.value))}
              fullWidth
            >
              {posOptions.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>
                  {pos.DepotName || pos.depot_name || pos.id}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <MoleDialogActions>
          <MoleCancelButton onClick={() => setToInvoice(null)}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
          <MolePrimaryButton onClick={() => void handleInvoice()} disabled={!selectedPos}>
            {t('common:offer.loadPos')}
          </MolePrimaryButton>
        </MoleDialogActions>
      </MoleDialog>

      <ConfirmDialog
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        isOpen={Boolean(toDelete)}
        setIsOpen={() => {}}
        title={<><DeleteOutline sx={{ color: 'red' }} /> {t('common:dialog.deleteConfirmation')}</>}
        message={t('common:offer.confirmDelete', { reference: toDelete?.reference })}
        confirmBtnColor="error"
        confirmBtnText={t('common:ui.buttons.delete')}
      />

      <ConfirmDialog
        onConfirm={handleCancel}
        onCancel={() => setToCancel(null)}
        isOpen={Boolean(toCancel)}
        setIsOpen={() => {}}
        title={t('common:ui.buttons.cancel')}
        message={t('common:offer.confirmCancel', { reference: toCancel?.reference })}
        confirmBtnColor="warning"
        confirmBtnText={t('common:ui.buttons.cancel')}
      />
    </>
    </OfferProvider>
  );
};

export default Offer;
